import { z } from "zod";

/**
 * Employee profile types (Builder / Connector / Craftsperson at the time of
 * writing) — admin-managed reference data edited at Settings → Config →
 * Employee profiles.
 *
 * Unlike `employment-types.ts` there is NO code-stable `key` here, and none is
 * needed: `person.employee_profile_id` is a real foreign key, so the row's
 * UUID is its identity and the name is free to change without disturbing
 * anything that points at it. Nothing in the application branches on a
 * specific profile, either — contrast `contractor`, which gates a field on the
 * person form.
 */

// Default-free base. Per the zod-v4 trap this project has hit before (WI-061),
// `.partial()` still fires `.default()`s, so no field here may carry one.
const employeeProfileFieldsSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(120, "Name is too long"),
  /**
   * Shown to readers beside the profile wherever it appears, which is the
   * reason this list is a table rather than an enum. "" is normalized to NULL
   * on the write path so a cleared textarea doesn't store an empty string.
   */
  description: z.string().trim().max(2000, "Description is too long").nullable().optional(),
  isActive: z.boolean().optional(),
});

export const employeeProfileCreateSchema = employeeProfileFieldsSchema;

export const employeeProfileUpdateSchema = employeeProfileFieldsSchema.partial();

export const employeeProfileReorderSchema = z.object({
  direction: z.enum(["up", "down"]),
});

export type EmployeeProfileCreateInput = z.infer<typeof employeeProfileCreateSchema>;
export type EmployeeProfileUpdateInput = z.infer<typeof employeeProfileUpdateSchema>;
